/**
 * Frontend structured logger.
 * In production, suppresses debug/info output.
 * In development, provides formatted console output with context.
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error'

const LOG_LEVELS: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3
}

const currentLevel: LogLevel = import.meta.env.PROD ? 'warn' : 'debug'

function shouldLog(level: LogLevel): boolean {
  return LOG_LEVELS[level] >= LOG_LEVELS[currentLevel]
}

function formatMessage(level: LogLevel, message: string, context?: Record<string, unknown>): string {
  const timestamp = new Date().toISOString().slice(11, 23)
  const prefix = `[${timestamp}] [${level.toUpperCase()}]`
  return context
    ? `${prefix} ${message} ${JSON.stringify(context)}`
    : `${prefix} ${message}`
}

export const logger = {
  debug(message: string, context?: Record<string, unknown>) {
    if (shouldLog('debug')) {
      console.debug(formatMessage('debug', message, context))
    }
  },

  info(message: string, context?: Record<string, unknown>) {
    if (shouldLog('info')) {
      console.info(formatMessage('info', message, context))
    }
  },

  warn(message: string, error?: unknown, context?: Record<string, unknown>) {
    if (shouldLog('warn')) {
      const warnContext = error instanceof Error
        ? { ...context, errorMessage: error.message, stack: error.stack }
        : error != null ? { ...context, error } : context
      console.warn(formatMessage('warn', message, warnContext))
    }
  },

  error(message: string, error?: unknown, context?: Record<string, unknown>) {
    if (shouldLog('error')) {
      const errorContext = error instanceof Error
        ? { ...context, errorMessage: error.message, stack: error.stack }
        : { ...context, error }
      console.error(formatMessage('error', message, errorContext))
    }
  }
}

export default logger
